import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { http } from '@/utils'
import { STORAGE_KEYS } from '@/constants'
import { useStorage } from '@vueuse/core'
import { useMutationData } from '@/composables'
import type { UserState, BindAccountByEmailRequest } from './types'
import { useAccountStore } from '@/store'
import type {
  LoginPayload,
  PersonalAccountRegisterPayload,
  CorporateAccountRegisterPayload,
  SuccessResult,
} from '@/api/auth/dto'
import type { GetCredentialCodeResponse, CredentialLoginRequest } from '@/api/third/dto'
import { MFAService } from '@/services/security/mfaService'
import { i18nRender } from '@/locales'
import type { EnsureResult } from '@/services/security/types'
import { isLocalhost, b64urlToArrayBuffer, arrayBufferToB64url } from '@/utils'

const { ACCESS_TOKEN, TOKEN_KEY, REFRESH_TOKEN, EXPIRES_IN, EXPIRES_AT, IS_LOGINED, USER_INFO } =
  STORAGE_KEYS

const initialInfo: UserState = {
  id: '',
  name: undefined,
  avatar: undefined,
  nickname: undefined,
  country: undefined,
  job: undefined,
  organization: undefined,
  location: undefined,
  email: undefined,
  introduction: undefined,
  personalWebsite: undefined,
  jobName: undefined,
  organizationName: undefined,
  locationName: undefined,
  phone: undefined,
  registrationDate: undefined,
  accountId: undefined,
  certification: undefined,
  role: '',
  create_time: undefined,
  is_company: undefined,
  remark: undefined,
  d_code: undefined,
  phone_verify: false,
  invent_code: '',
  kyc_status: undefined,
  bind_google_secret: undefined,
  credential: null,
  userType: undefined,
  bind_withdraw_password: undefined,
}

const useUserStore = defineStore('user', () => {
  // —— 单一真相源（SSOT）：登录态与 Token 都在这里 —— //
  const isLoggedIn = useStorage(IS_LOGINED, false)
  const userInfo = useStorage(USER_INFO, { ...initialInfo })
  const userInfoLoading = ref(false)

  // 对外暴露 Token（可选，但通常很有用）
  const accessToken = useStorage(ACCESS_TOKEN, '')
  const refreshToken = useStorage(REFRESH_TOKEN, '')
  const token = useStorage(TOKEN_KEY, '')
  const tokenExpireAt = useStorage(EXPIRES_AT, 0)
  const tokenExpireIn = useStorage(EXPIRES_IN, 0)

  // 派生字段
  const userId = computed(() => userInfo.value.id ?? '')
  const userEmail = computed(() => userInfo.value.email ?? '')
  const userName = computed(() => userInfo.value.name ?? '')
  const userNickName = computed(() => userInfo.value.nickname ?? '')
  const userPhone = computed(() => userInfo.value.phone || '')
  const userAvatar = computed(() => userInfo.value.avatar ?? '')
  const userCountry = computed(() => userInfo.value.country ?? '')
  const userBiography = computed(() => userInfo.value.remark ?? '')
  const userDCode = computed(() => userInfo.value.d_code ?? '')
  const bindGoogleSecret = computed(() => userInfo.value.bind_google_secret)
  const credential = computed(() => userInfo.value.credential)
  const userType = computed(() => userInfo.value.userType ?? undefined)
  const isCompany = computed(() => userInfo.value.is_company == '1')
  const hasBindWithdrawPassword = computed(() => userInfo.value.bind_withdraw_password == '1')
  const hasVerifiedKyc = computed(() => userInfo.value.kyc_status == 9)
  const hasVerifiedPhone = computed(() => userInfo.value.phone_verify == true)
  const hasBindApp = computed(() => userInfo.value.bind_google_secret == '1')
  const hasBindPasskey = computed(
    () => Array.isArray(userInfo.value.credential) && userInfo.value.credential.length > 0,
  )
  const hasBindApplePasskey = computed(
    () => hasBindPasskey.value && userInfo.value.credential.join(',').includes('1'),
  )
  const hasBindGooglePasskey = computed(
    () => hasBindPasskey.value && userInfo.value.credential.join(',').includes('2'),
  )
  const setUserInfo = (data: Partial<UserState>) => {
    userInfo.value = { ...userInfo.value, ...data }
  }

  const reset = () => {
    isLoggedIn.value = false
    userInfo.value = { ...initialInfo }
    accessToken.value = ''
    token.value = ''
    refreshToken.value = ''
    tokenExpireAt.value = 0
    tokenExpireIn.value = 0
    userInfoLoading.value = false
  }

  const updateNickname = async (nickname: string) => {
    userInfo.value.nickname = nickname
  }

  const updateAvatar = async (avatar: string) => {
    userInfo.value.avatar = avatar
  }

  const updateBiography = async (biography: string) => {
    userInfo.value.remark = biography
  }

  const login = async (loginFormData: LoginPayload) => {
    try {
      const data: SuccessResult = await http.post('/api/pub/login', loginFormData)
      await onAuthSuccess(data)
    } catch (e: any) {
      if (e?.message === 'MFA_REQUIRED' && e?.mfa?.securityCode) {
        const result = await MFAService.ensure(e.mfa.securityCode, {
          title: i18nRender('common.mfa.remoteLogin.title'),
          payload: { request: loginFormData, data: e.mfa.data },
        })
        const { ok, passageKey, submitCleanFn } = result as EnsureResult
        if (!ok) throw new Error('MFA_REJECTED')

        const data = await http.call<SuccessResult>({
          method: (e.mfa.originalCfg?.method as any) ?? 'post',
          url: e.mfa.originalCfg?.url!,
          data: e.mfa.originalCfg?.data ?? loginFormData,
          requestConfig: {
            headers: {
              ...((e.mfa.originalCfg?.headers as any) || {}),
              passageKey: passageKey,
            },
          },
          useAuth: (e.mfa.originalCfg as any)?.useAuth,
        })
        if (submitCleanFn && typeof submitCleanFn === 'function') submitCleanFn()
        await onAuthSuccess(data)
        return
      }

      // 复杂逻辑：非 MFA 场景（网络错误、业务错误等），抛给调用方处理
      throw e
    }
  }

  const register = async (
    regisData: PersonalAccountRegisterPayload | CorporateAccountRegisterPayload,
  ) => {
    // 关键：如果当前已登录，先清理所有store状态，避免新老用户数据混乱
    // 使用动态导入避免循环依赖，清理所有store确保数据干净
    if (isLoggedIn.value) {
      console.warn('[userStore.register] 检测到已登录状态，清理所有store中...')
      const { resetAllStores } = await import('@/store')
      resetAllStores()
    }

    const data: SuccessResult = await http.post('/api/pub/reg', regisData)
    await onAuthSuccess(data)
  }

  const bindAccount = async (payload: BindAccountByEmailRequest) => {
    // 同样需要清理所有store状态（第三方登录绑定账户场景）
    // 使用动态导入避免循环依赖，确保绑定时数据干净
    if (isLoggedIn.value) {
      console.warn('[userStore.bindAccount] 检测到已登录状态，清理所有store中...')
      const { resetAllStores } = await import('@/store')
      resetAllStores()
    }

    const data: SuccessResult = await http.post('/api/third/bindMember', payload, {
      useAuth: true,
    })
    await onAuthSuccess(data)
  }

  // Passkey flows
  const { loading: passkeyLoading, execute: getCredentialCode } = useMutationData(
    async () =>
      await http.post<GetCredentialCodeResponse, GetCredentialCodeResponse>(
        '/api/third/get_login_credential_code',
        undefined,
        { useAuth: false },
      ),
    { showMessage: false, debounceTime: 0 },
  )

  const { loading: credentialLoading, execute: loginByCredential } = useMutationData(
    async (payload: CredentialLoginRequest) =>
      await http.post<CredentialLoginRequest, SuccessResult>(
        '/api/third/login_by_credential',
        payload,
        { useAuth: false },
      ),
    { showMessage: false, debounceTime: 0 },
  )

  const handlePasskeyLogin = async () => {
    if (!('credentials' in navigator) || !(window as any).PublicKeyCredential) {
      window.GtcNotification('error', i18nRender('login.passkey.notSupported'))
      return
    }
    try {
      const data = await getCredentialCode()
      if (!data) throw new Error(i18nRender('login.passkey.getCredentialFailed'))
      const { options, options_token } = data
      const publicKey: PublicKeyCredentialRequestOptions = {
        challenge: b64urlToArrayBuffer(String(options.challenge)),
        rpId: isLocalhost() ? 'localhost' : (options.rpId ?? undefined),
        allowCredentials: (options.allowCredentials || []).map((c: any) => ({
          id: b64urlToArrayBuffer(c.id),
          type: (c.type as PublicKeyCredentialType) || 'public-key',
          transports: c.transports as AuthenticatorTransport[] | undefined,
        })),
        userVerification: (options.userVerification as UserVerificationRequirement) || 'preferred',
        timeout: options.timeout ?? undefined,
        extensions: options.extensions as any,
      }
      const cred = (await navigator.credentials.get({ publicKey })) as PublicKeyCredential
      if (!cred) {
        window.GtcNotification('info', i18nRender('login.passkey.cancelled'))
        return
      }
      const assertion = cred.response as AuthenticatorAssertionResponse

      const payload: CredentialLoginRequest = {
        id: cred.id,
        rawId: arrayBufferToB64url(cred.rawId),
        type: cred.type,
        options_token,
        response: {
          clientDataJSON: arrayBufferToB64url(assertion.clientDataJSON),
          authenticatorData: arrayBufferToB64url(assertion.authenticatorData),
          signature: arrayBufferToB64url(assertion.signature),
          userHandle: assertion.userHandle ? arrayBufferToB64url(assertion.userHandle) : null,
        },
      }
      const result = await loginByCredential(payload)
      if (!result) throw new Error(i18nRender('login.passkey.loginFailed'))
      onAuthSuccess(result)
    } catch (err: any) {
      throw err
    }
  }

  const fetchUserInfo = async () => {
    userInfoLoading.value = true
    const data = await http.post<UserState>('/api/member/info')
    userInfo.value = { ...initialInfo, ...data }
    userInfoLoading.value = false
  }

  // 统一 Token 落盘 + 后续初始化
  const onAuthSuccess = async (data: SuccessResult) => {
    const { access_token, expire_at, refresh_token } = data
    accessToken.value = access_token
    refreshToken.value = refresh_token
    token.value = access_token
    tokenExpireIn.value = expire_at // 秒
    tokenExpireAt.value = Date.now() + expire_at * 1000
    isLoggedIn.value = true

    // 告知 http 层（若支持）
    http.setAuthToken?.(access_token)

    // 拉取个人信息
    try {
      await fetchUserInfo()
    } catch (error) {
      console.warn('[userStore] fetchUserInfo failed:', error)
    }

    // 初始化账户
    const accountStore = useAccountStore()
    await accountStore.init({ withCategory: true, withExternal: true })
  }

  return {
    // state
    isLoggedIn,
    userInfo,
    accessToken,
    refreshToken,
    token,
    tokenExpireAt,
    tokenExpireIn,
    userInfoLoading,
    // getters
    userId,
    userEmail,
    userName,
    userNickName,
    userPhone,
    userAvatar,
    userCountry,
    userBiography,
    userDCode,
    bindGoogleSecret,
    credential,
    userType,
    isCompany,
    hasVerifiedKyc,
    hasBindWithdrawPassword,
    hasVerifiedPhone,
    hasBindApp,
    hasBindPasskey,
    hasBindGooglePasskey,
    hasBindApplePasskey,

    // actions
    updateNickname,
    updateAvatar,
    updateBiography,
    setUserInfo,
    fetchUserInfo,
    reset,
    handlePasskeyLogin,

    // auth flows
    login,
    register,
    bindAccount,
    passkeyLoading,
    credentialLoading,
    getCredentialCode,
    loginByCredential,
    onAuthSuccess,
  }
})

export { useUserStore }
export default useUserStore
